import { view } from "@risingstack/react-easy-state";
import { IconArrowForward } from "@tine/designsystem-icons/sharp";
import { Checkbox, Grid, Stack } from "@tine/designsystem-ui-react";
import { type ComponentType, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { isDataAvailable, isDefaultData, isWaitingForData } from "../common/utils/asyncDataUtils";
import { getCaptureConfig, getCaptureMappings } from "../common/utils/dynamicsUtils";
import SignupForm from "../components/common/signup/SignupForm";

import authStore from "../stores/auth/authStore";
import contentStore from "../stores/cms/contentStore";
import promotionStore from "../stores/promotionStore";

import ErrorAlertWithOptionalTracking from "../components/ErrorAlertWithOptionalTracking";
import { ExternalLink, InternalLink } from "../components/Links";
import CmsContentFetchWrapper from "../components/cms/CmsContentFetchWrapper";
import { AdventCalendarHeader } from "../components/cms/advent-calendar/AdventCalendarHeader";
import InformationBox from "../components/common/informationBox/InformationBox";

export const ADVENT_CALENDAR_SLUG = "julekalender";
const calendarFooterImage = new URL("../../img/julekalender_footer.png", import.meta.url).toString();
const mainSignupCode = "julekalender-2024-hovedpamelding";

const ContestInfoBox: ComponentType = () => {
   return (
      <InformationBox theme="christmas" title="Vinn premier hver dag" className="tw-text-center">
         <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <p>Delta i julekalenderen og bli med i trekningen av spennende premier!</p>{" "}
            <p>
               For hver dag du deltar får du også et lodd til hovedtrekningen på julaften. Jo flere dager du deltar, desto større
               vinnersjanse.
            </p>
            <div className="tw-flex tw-flex-row tw-justify-center tw-gap-1">
               <InternalLink variant="inverted" href="/julekalender/vinnere">
                  Se vinnere
               </InternalLink>
               <IconArrowForward />
            </div>
         </div>
      </InformationBox>
   );
};

const TermsAndConditionsLinks: ComponentType = () => (
   <>
      <InternalLink href="/julekalender/vilkar" variant="inverted">
         vilkårene
      </InternalLink>{" "}
      og{" "}
      <ExternalLink
         variant="inverted"
         href="https://www.tine.no/kontakt/artikler/ansvarserkl%C3%A6ring-personvern-og-debattregler-p%C3%A5-tines-nettsteder"
      >
         personvernerklæringen
      </ExternalLink>
      .
   </>
);

const ContestFormBox: ComponentType = view(() => {
   const [consent, setConsent] = useState<boolean>(false);
   const company = authStore.getCurrentCompany();
   const isSignedUp = promotionStore.isSignedUpTo(mainSignupCode);
   const isLoggedIn = authStore.isLoggedIn();

   const values = {
      company: company?.customerNumber || "",
      description: "Julekalender 2024 - hovedpåmelding"
   };
   const companyName = company?.name || "Ditt selskap";

   const handleSignup = (payload: Record<string, string>) => {
      promotionStore.signUpToPromotion(mainSignupCode, "", "", payload);
   };
   return (
      <InformationBox
         theme="christmas"
         title={isLoggedIn && isSignedUp ? "Hovedtrekningen" : "Meld deg på hovedtrekningen"}
         className="tw-text-center"
      >
         <div className="tw-flex tw-flex-col tw-gap-3">
            <>
               <p>
                  {isLoggedIn && isSignedUp ? `${companyName} er` : "Bli"} med i trekningen av gavekort på kr 5000,- til
                  sosialkassen.
               </p>
               {!isLoggedIn ? (
                  <>
                     <p>
                        Mer informasjon finnes i <TermsAndConditionsLinks />
                     </p>
                     <p>Du må logge inn for å delta.</p>
                  </>
               ) : (
                  <>
                     <p className="tw-mb-0.5">Øk din vinnersjanse ved å:</p>
                     <ul className="tw-text-left">
                        <li>Melde deg på hovedtrekningen før 1. desember for 2 lodd, eller fra og med 1. desember for 1 lodd</li>
                        <li>Delta i dagens kalenderluke for 1 lodd per dag</li>
                        <li>Følge TINE Partner på Instagram for 1 lodd</li>
                     </ul>{" "}
                  </>
               )}

               {isLoggedIn &&
                  (!isSignedUp ? (
                     <SignupForm
                        buttonText="Delta i trekningen"
                        config={getCaptureConfig("main")}
                        mappings={getCaptureMappings("main")}
                        onSignup={handleSignup}
                        values={values}
                        disabled={!consent}
                        buttonVariant="secondary"
                        trackingFormName="Hovedpåmelding julekalender"
                     >
                        <Checkbox
                           description={
                              <p className="tw-text-left tw-text-ink-brand-inverted">
                                 Jeg bekrefter at jeg har lest og forstått <TermsAndConditionsLinks />
                              </p>
                           }
                           isChecked={consent}
                           onChange={() => setConsent(!consent)}
                        />
                     </SignupForm>
                  ) : (
                     <p>
                        Mer informasjon finnes i <TermsAndConditionsLinks />
                     </p>
                  ))}
            </>
         </div>
      </InformationBox>
   );
});

const AdventCalendarPage: ComponentType = () => {
   if (isWaitingForData(contentStore.urlData) || isDefaultData(contentStore.urlData)) {
      return <Skeleton height={500} />;
   }

   if (isDataAvailable(contentStore.urlData)) {
      const urlData = contentStore.urlData.data.find((data) => data.slug === ADVENT_CALENDAR_SLUG);
      if (!urlData) {
         return (
            <ErrorAlertWithOptionalTracking
               showCloseButton={false}
               gaErrorTracking={{ context: "advent_calendar_page", text: "content_not_found_error" }}
            >
               Fant ikke innholdet med slug '{ADVENT_CALENDAR_SLUG}'
            </ErrorAlertWithOptionalTracking>
         );
      }

      return (
         <Grid cols={12} gap={4}>
            <Grid.Col span={12}>
               <AdventCalendarHeader />
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }}>
               <Stack>
                  <ContestInfoBox />
                  <div className="tw-hidden lg:tw-block">
                     <ContestFormBox />
                  </div>
               </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 8 }}>
               <CmsContentFetchWrapper id={urlData.id} />
            </Grid.Col>

            <Grid.Col span={12} className="tw-block lg:tw-hidden">
               <ContestFormBox />
            </Grid.Col>

            <Grid.Col span={12}>
               <div className="tw-h-[73px] tw-w-full">
                  <img className="tw-size-full tw-object-cover" src={calendarFooterImage} alt="Julekalender-footer" />
               </div>
            </Grid.Col>
         </Grid>
      );
   }
   return (
      <ErrorAlertWithOptionalTracking
         showCloseButton={false}
         gaErrorTracking={{ context: "advent_calendar_page", text: "loading_error" }}
      >
         Det oppsto en uventet feil under lasting av forsiden
      </ErrorAlertWithOptionalTracking>
   );
};

export default view(AdventCalendarPage);
